import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import Users from './pages/Users';
import Settings from './pages/Settings';
import Logs from './pages/Logs';
import PaymentsLinks from './pages/PaymentsLinks';
import Sidebar from './components/Sidebar';
import VirtualClasses from './pages/VirtualClasses';
import ScheduledEmails from './pages/ScheduledEmails';
import LearnyboxMembers from './pages/LearnyboxMembers';
import PublicVirtualClassAccess from './pages/PublicVirtualClassAccess';
import SignaturePage from './pages/SignaturePage';
import AdminUsersPage from './pages/AdminUsersPage';
import RolesPage from './pages/RolesPage';
import InscritsFormation from './pages/InscritsFormation';
import EmailTemplates from './pages/EmailTemplates';
import EmailFilter from './pages/EmailFilter';
import Profile from './pages/Profile';
import StudentHourCredits from './pages/StudentHourCredits';
import VirtualClassAccessStats from './pages/VirtualClassAccessStats';
import KommoOverview from './pages/kommo/KommoOverview';
import KommoLeads from './pages/kommo/KommoLeads';
import KommoSellers from './pages/kommo/KommoSellers';
import KommoSellerDetail from './pages/kommo/KommoSellerDetail';
import KommoTags from './pages/kommo/KommoTags';
import KommoLeadTimeline from './pages/kommo/KommoLeadTimeline';
import KommoSellerEvents from './pages/kommo/KommoSellerEvents';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from 'sonner';
import { cn } from './lib/utils';

// A ProtectedRoute component
const ProtectedRoute = ({ children, permission }: { children: React.ReactNode, permission?: string }) => {
  const { isAuthenticated, loading, hasPermission } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 gap-4 animate-in fade-in duration-500">
        <div className="relative">
          <div className="h-16 w-16 rounded-full border-4 border-slate-200 border-t-primary animate-spin" />
          <div className="absolute inset-x-0 -bottom-8 flex justify-center">
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">Initialisation...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (permission && !hasPermission(permission)) {
    return <Navigate to="/" replace />; // Or a 403 page
  }

  return children;
};

const AppContent = () => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const isPublicRoute = location.pathname === '/classe-virtuelle' || location.pathname === '/classe-virtuelle/signature';

  return (
    <div className="flex min-h-screen bg-slate-50 font-sans text-slate-900 selection:bg-primary/10 selection:text-primary">
      {isAuthenticated && !loading && !isPublicRoute && <Sidebar />}
      <div className={cn(
        "flex-1 overflow-y-auto h-screen",
        isAuthenticated && !loading && !isPublicRoute ? "ml-64" : ""
      )}>
        <Routes>
          <Route path="/classe-virtuelle/signature" element={<SignaturePage />} />
          <Route path="/classe-virtuelle" element={<PublicVirtualClassAccess />} />
          <Route path="/login" element={<Login />} />
          <Route
            path="/"
            element={
              <ProtectedRoute permission="view dashboard">
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute permission="view users">
                <Users />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute permission="view settings">
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/logs"
            element={
              <ProtectedRoute permission="view logs">
                <Logs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments-links"
            element={
              <ProtectedRoute permission="view payments">
                <PaymentsLinks />
              </ProtectedRoute>
            }
          />
          <Route
            path="/virtual-classes"
            element={
              <ProtectedRoute permission="view virtual classes">
                <VirtualClasses />
              </ProtectedRoute>
            }
          />
          <Route
            path="/virtual-classes/access-stats"
            element={
              <ProtectedRoute permission="view class stats">
                <VirtualClassAccessStats />
              </ProtectedRoute>
            }
          />
          <Route
            path="/scheduled-emails"
            element={
              <ProtectedRoute permission="view scheduled emails">
                <ScheduledEmails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/learnybox-members"
            element={
              <ProtectedRoute permission="view learnybox">
                <LearnyboxMembers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute permission="manage users">
                <AdminUsersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/roles"
            element={
              <ProtectedRoute permission="manage roles">
                <RolesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inscrits-formation"
            element={
              <ProtectedRoute permission="view inscrits formation">
                <InscritsFormation />
              </ProtectedRoute>
            }
          />
          <Route
            path="/student-hours"
            element={
              <ProtectedRoute permission="manage student hours">
                <StudentHourCredits />
              </ProtectedRoute>
            }
          />
          <Route
            path="/email-filter"
            element={
              <ProtectedRoute permission="view settings">
                <EmailFilter />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/email-templates"
            element={
              <ProtectedRoute permission="view settings">
                <EmailTemplates />
              </ProtectedRoute>
            }
          />
          {/* Kommo Analytics */}
          <Route
            path="/kommo"
            element={
              <ProtectedRoute permission="view kommo analytics">
                <KommoOverview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/kommo/leads"
            element={
              <ProtectedRoute permission="view kommo analytics">
                <KommoLeads />
              </ProtectedRoute>
            }
          />
          <Route
            path="/kommo/sellers"
            element={
              <ProtectedRoute permission="view kommo analytics">
                <KommoSellers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/kommo/sellers/:kommoUserId"
            element={
              <ProtectedRoute permission="view kommo analytics">
                <KommoSellerDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/kommo/sellers/:kommoUserId/events"
            element={
              <ProtectedRoute permission="view kommo analytics">
                <KommoSellerEvents />
              </ProtectedRoute>
            }
          />
          <Route
            path="/kommo/tags"
            element={
              <ProtectedRoute permission="view kommo analytics">
                <KommoTags />
              </ProtectedRoute>
            }
          />
          <Route
            path="/kommo/lead/:kommoId"
            element={
              <ProtectedRoute permission="view kommo analytics">
                <KommoLeadTimeline />
              </ProtectedRoute>
            }
          />
          <Route
            path="*"
            element={
              loading ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className="h-8 w-8 rounded-full border-2 border-slate-200 border-t-primary animate-spin" />
                </div>
              ) : (
                isAuthenticated ? <Navigate to="/" replace /> : <Navigate to="/login" replace />
              )
            }
          />
        </Routes >
      </div >
      <Toaster
        position="top-center"
        expand={true}
        richColors={true}
        closeButton={true}
      />
    </div >
  );
}

function App() {
  return (
    <AuthProvider>
      <Router basename="/">
        <AppContent />
      </Router>
    </AuthProvider>
  );
}

export default App;
